import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, FileCheck, X, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_MB = 50;
// pdfjs-dist ships its worker as a separate file. Loading it from jsDelivr
// keeps the CRA webpack config untouched and matches the pdfjs version we
// installed via yarn.
const PDFJS_VERSION = "4.10.38";
const PDFJS_WORKER_URL = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

// Render the first page of a PDF to a small JPG data URL. Returns null on
// failure — the caller falls back to a generic file-icon chip.
async function renderPdfThumbnail(file, maxDim = 240) {
  try {
    const pdfjs = await import("pdfjs-dist/build/pdf.mjs");
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    const page = await doc.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    // Downscale so the largest side fits within maxDim (thumb is only 96px in
    // the UI but we render 2x for crisp DPI).
    const scale = maxDim / Math.max(baseViewport.width, baseViewport.height);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    // White background so transparent PDFs don't render as black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: ctx, viewport }).promise;
    try { doc.destroy(); } catch { /* noop */ }
    return canvas.toDataURL("image/jpeg", 0.72);
  } catch (e) {
    console.warn("PDF thumbnail failed:", e);
    return null;
  }
}

/**
 * Prompts the buyer to attach their print-ready PDF right on the product
 * page. On success the parent gets `{ artwork_token, artwork_filename,
 * artwork_size }` which flows through the cart line into the order so the
 * buyer skips the post-checkout email upload step entirely.
 *
 * A first-page PDF thumbnail is rendered client-side (via pdfjs-dist) so
 * buyers instantly see "yes, that's the right file" — big drop in
 * "did I upload the wrong file?" support emails.
 *
 * `value` shape (or null): { artwork_token, artwork_filename, artwork_size }.
 */
export default function ArtworkUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  // Thumbnail is browser-only session state — a returning buyer sees no
  // preview until they re-upload. Acceptable trade-off vs. server-side
  // thumbnail generation which needs a PDF rasterizer install.
  const [thumbnail, setThumbnail] = useState(null);
  const inputRef = useRef(null);

  // If the parent clears `value` (e.g. after add-to-cart), drop the local thumb too.
  useEffect(() => { if (!value?.artwork_token) setThumbnail(null); }, [value]);

  const upload = useCallback(async (file) => {
    setError(null);
    if (!file) return;
    if (!/\.pdf$/i.test(file.name) && file.type !== "application/pdf") {
      setError("Only PDF files are accepted.");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      setError(`File is too large — max ${MAX_MB} MB.`);
      return;
    }
    setUploading(true);
    // Render the first-page thumbnail in parallel with the network upload so
    // the user sees the preview the moment their file is confirmed.
    const thumbPromise = renderPdfThumbnail(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const [uploadResult, thumb] = await Promise.all([
        axios.post(`${API}/checkout/artwork/prepare-upload`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000,
        }),
        thumbPromise,
      ]);
      const data = uploadResult.data;
      setThumbnail(thumb);
      onChange({
        artwork_token: data.artwork_token,
        artwork_filename: data.artwork_filename,
        artwork_size: data.artwork_size,
      });
      toast.success("Artwork attached — will ship with your order.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Upload failed";
      setError(detail);
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const removeFile = useCallback(async () => {
    const token = value?.artwork_token;
    onChange(null);
    setError(null);
    if (!token) return;
    // Best-effort cleanup — we don't care if this fails, the token becomes
    // orphaned but won't be redeemed at checkout so it's inert.
    try {
      await axios.delete(`${API}/checkout/artwork/prepare-upload/${token}`);
    } catch { /* ignore */ }
  }, [value, onChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  }, [upload]);

  // ----- Attached state -----
  if (value?.artwork_token) {
    const mb = (value.artwork_size / (1024 * 1024)).toFixed(2);
    return (
      <div
        className="card-dark border-emerald-500/40 bg-emerald-500/5 p-4 flex items-center gap-4"
        data-testid="artwork-attached"
      >
        {thumbnail ? (
          <div
            className="w-16 h-20 shrink-0 rounded-sm overflow-hidden border border-emerald-500/40 bg-white"
            data-testid="artwork-thumbnail"
          >
            <img src={thumbnail} alt="First page preview" className="w-full h-full object-contain" />
          </div>
        ) : (
          <div className="w-16 h-20 shrink-0 rounded-sm border border-emerald-500/40 bg-emerald-500/10 flex items-center justify-center">
            <FileCheck size={22} className="text-emerald-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate" data-testid="artwork-filename">{value.artwork_filename}</div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 mt-1">
            {mb} MB · Attached to your order
          </div>
          {thumbnail && (
            <div className="text-[10px] text-white/45 mt-1">
              Preview shows the first page — flip through your PDF to check other pages.
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={removeFile}
          data-testid="artwork-remove"
          className="p-1.5 text-white/50 hover:text-red-400 transition-colors self-start"
          title="Remove artwork"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // ----- Upload state -----
  return (
    <div
      className={`card-dark border-2 border-dashed p-6 transition-colors ${
        dragging ? "border-gold bg-gold/5" : "border-white/15 hover:border-gold/40"
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      data-testid="artwork-dropzone"
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        data-testid="artwork-file-input"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
      />
      <div className="flex items-start gap-4">
        <div className={`w-11 h-11 rounded-sm flex items-center justify-center shrink-0 ${uploading ? "bg-gold/10" : "bg-white/5"}`}>
          {uploading ? <Loader2 size={20} className="text-gold animate-spin" /> : <UploadCloud size={20} className="text-gold" />}
        </div>
        <div className="flex-1">
          <div className="font-display text-white text-base">
            {uploading ? "Uploading your artwork…" : "Attach your artwork (optional)"}
          </div>
          <p className="text-white/60 text-xs mt-1 leading-relaxed">
            Drop a print-ready PDF here or{" "}
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="text-gold underline underline-offset-2 hover:text-white"
              disabled={uploading}
              data-testid="artwork-browse"
            >
              browse files
            </button>
            . Up to {MAX_MB} MB. Skip this and we&apos;ll email you an upload link after checkout.
          </p>
          {error && (
            <div className="mt-3 flex items-start gap-2 text-red-400 text-xs" data-testid="artwork-error">
              <AlertCircle size={12} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
